import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
// @ts-ignore
import { sys } from '@netbasequid/theme'
import { useInsightContentData } from 'hooks/useInsightModalState'
import { InsightContentSettingType } from 'components/insightContent/types'
import InsightContentType from 'components/insightContent/enum/InsightContentType'
import SparklineOverview from 'components/insightContent/visualization/Overview/SparklineOverview'
import { ServiceResultWith } from 'schemas/ServiceResultSchema'
import { AnnotatedMetricResult } from 'schemas/AnnotatedMetricResultSchema'
import { Status } from 'features/types'
import { getSparklineMetricTimeUnit } from 'utils/sparklineMetricTimeUnit'
import { getPercentOfTotal } from 'components/insightContent/utils/utils'
import { formatMetricSeriesType } from 'utils/metricSeriesTypeUtil'
import { Theme } from 'features/ui/types'
import { useDisplayTheme } from 'hooks/useUiState'
import { Emphasized } from 'components/insightContent/visualization/Overview/index.css'
import { MeasureType, MessageConst, ReturnStatus } from 'enums'
import messageUtil from 'utils/messageUtil'
import { getMetricSeries } from 'utils/measureTypeUtil'
import { AutomationId } from 'utils/automationUtil'
import RunSearchEffect from './RunSearchEffect'
import { useTopicsResult, useSelectedTopicResult, useSelectedTopicStatus } from './hooks'
import { getMainMeasure } from './utils'

const Container = styled.div<{ mode: Theme }>`
  margin: 0px -16px;
  & > div {
    padding: 16px 16px;
  }
  & > div:not(:first-child) {
    border-top: 1px solid ${({ mode }) => sys[mode].common.divider[100]};
  }
`

const measureTypeToTestId = {
  [MeasureType.IMPRESSIONS]: AutomationId.insightModal.insightContent.topicComparison.impressions,
  [MeasureType.PASSION_INTENSITY]:
    AutomationId.insightModal.insightContent.topicComparison.passionIntensity,
  [MeasureType.NET_SENTIMENT]: AutomationId.insightModal.insightContent.topicComparison.netSentiment
}

interface TopicComparisonProps {
  detailSetting: InsightContentSettingType
  isAudienceQuery?: boolean
}

const TopicComparison = ({ detailSetting, isAudienceQuery }: TopicComparisonProps) => {
  const theme = useDisplayTheme()
  const { t } = useTranslation()
  const insightContent = useInsightContentData<{
    status: Status
    data: ServiceResultWith<AnnotatedMetricResult>[]
  }>(InsightContentType.TOPIC_COMPARISON)

  const mainMeasure = getMainMeasure(detailSetting.viewOption, isAudienceQuery)
  const mainMetricSeries = getMetricSeries(mainMeasure)[0]
  const trendTimeUnit = getSparklineMetricTimeUnit(detailSetting?.filter?.publishedDate)

  // topics
  const topicsServiceResults = useMemo(
    () => insightContent?.data?.slice(0, detailSetting.topicInfos.length),
    [insightContent, detailSetting]
  )
  const [topicsTotalResult, topicsTrendResult] = useTopicsResult(
    topicsServiceResults,
    trendTimeUnit
  )

  // selected topic
  const selectedTopicServiceResult = useMemo(
    () => insightContent?.data?.slice(detailSetting.topicInfos.length)[0],
    [insightContent, detailSetting]
  )
  const [selectedTopicTotalResult, selectedTopicTrendResult] = useSelectedTopicResult(
    selectedTopicServiceResult,
    trendTimeUnit
  )
  const [selectedTopicTotalStatus, selectedTopicTrendStatus] = useSelectedTopicStatus(
    selectedTopicServiceResult,
    trendTimeUnit
  )

  return (
    <Container mode={theme}>
      <RunSearchEffect detailSetting={detailSetting} isAudienceQuery={isAudienceQuery}>
        <SparklineOverview
          status={insightContent?.status}
          title={t('totalForAllTopics', {
            measure: messageUtil.measureType(mainMeasure)
          })}
          count={topicsTotalResult[mainMetricSeries]?.[0]}
          sparklineData={topicsTrendResult[mainMetricSeries]}
          measureType={mainMeasure}
          testId={AutomationId.insightModal.insightContent.topicComparison.allTopics}
        />
        <SparklineOverview
          status={insightContent?.status}
          title={
            <>
              {`${messageUtil.measureType(mainMeasure)} ${t('for')}`}{' '}
              <Emphasized>{detailSetting.selectionName}</Emphasized>
            </>
          }
          count={selectedTopicTotalResult[mainMetricSeries]?.[0]}
          percent={getPercentOfTotal(
            selectedTopicTotalResult[mainMetricSeries]?.[0],
            Number(topicsTotalResult[mainMetricSeries]?.[0])
          )}
          sparklineData={selectedTopicTrendResult[mainMetricSeries]}
          measureType={mainMeasure}
          testId={AutomationId.insightModal.insightContent.topicComparison.selectedTopic}
        />
        {[MeasureType.IMPRESSIONS, MeasureType.PASSION_INTENSITY, MeasureType.NET_SENTIMENT].map(
          (measure) => {
            const metricSeries = getMetricSeries(measure)[0]
            return (
              <SparklineOverview
                key={measure}
                status={insightContent?.status}
                title={
                  <>
                    {`${messageUtil.measureType(measure)} ${t('for')}`}{' '}
                    <Emphasized>{detailSetting.selectionName}</Emphasized>
                  </>
                }
                count={
                  selectedTopicTotalStatus[metricSeries] === ReturnStatus.LANGUAGE_NOT_SUPPORTED
                    ? MessageConst.EM_DASH
                    : formatMetricSeriesType(
                        metricSeries,
                        selectedTopicTotalResult?.[metricSeries]?.[0]
                      )
                }
                sparklineData={
                  selectedTopicTrendStatus[metricSeries] === ReturnStatus.LANGUAGE_NOT_SUPPORTED
                    ? undefined
                    : selectedTopicTrendResult?.[metricSeries]
                }
                measureType={measure}
                testId={measureTypeToTestId[measure]}
              />
            )
          }
        )}
      </RunSearchEffect>
    </Container>
  )
}

export default TopicComparison
